// IndexedDB utilities for offline storage
const DB_NAME = 'AthleteTrackerOffline';
const DB_VERSION = 1;
const ATHLETES_STORE = 'athletes';
const RECORDS_STORE = 'records';
const SYNC_QUEUE_STORE = 'syncQueue';

// Initialize IndexedDB
export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create athletes store
      if (!db.objectStoreNames.contains(ATHLETES_STORE)) {
        const athletesStore = db.createObjectStore(ATHLETES_STORE, { keyPath: 'id' });
        athletesStore.createIndex('name', 'name', { unique: false });
      }

      // Create records store
      if (!db.objectStoreNames.contains(RECORDS_STORE)) {
        const recordsStore = db.createObjectStore(RECORDS_STORE, { keyPath: 'id' });
        recordsStore.createIndex('athlete_id', 'athlete_id', { unique: false });
        recordsStore.createIndex('date', 'date', { unique: false });
      }

      // Create sync queue store
      if (!db.objectStoreNames.contains(SYNC_QUEUE_STORE)) {
        const syncStore = db.createObjectStore(SYNC_QUEUE_STORE, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

// Get all items from a store
export const getAllFromStore = async (storeName) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Get single item by ID
export const getFromStore = async (storeName, id) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Add or update item in store
export const saveToStore = async (storeName, data) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(data);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Delete item from store
export const deleteFromStore = async (storeName, id) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Get records by athlete ID
export const getRecordsByAthleteId = async (athleteId) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(RECORDS_STORE, 'readonly');
    const store = transaction.objectStore(RECORDS_STORE);
    const index = store.index('athlete_id');
    const request = index.getAll(athleteId);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Clear all data from a store
export const clearStore = async (storeName) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Sync Queue Operations
export const addToSyncQueue = async (operation) => {
  const queueItem = {
    ...operation,
    timestamp: new Date().toISOString()
  };
  return await saveToStore(SYNC_QUEUE_STORE, queueItem);
};

export const getSyncQueue = async () => {
  return await getAllFromStore(SYNC_QUEUE_STORE);
};

export const removeFromSyncQueue = async (id) => {
  return await deleteFromStore(SYNC_QUEUE_STORE, id);
};

export const clearSyncQueue = async () => {
  return await clearStore(SYNC_QUEUE_STORE);
};

// Store names export for use in other files
export const STORES = {
  ATHLETES: ATHLETES_STORE,
  RECORDS: RECORDS_STORE,
  SYNC_QUEUE: SYNC_QUEUE_STORE
};
